import type { Level, LogEvent } from 'pino';
import { levels as standardLevels } from 'pino';
import metaInformation from './metaInformation';

type CustomLevel = Level | 'metric' | 'analytic';

type Payload = {
    level: CustomLevel;
    msg: string;
    [key: string]: string;
};

const payload: Payload[] = [];

export async function flushBeaconQueue() {
    if (!process.env['NEXT_PUBLIC_LOG_BEACON_URL']) {
        return Promise.reject(
            new Error('Missing NEXT_PUBLIC_LOG_BEACON_URL value')
        );
    }

    if (!payload.length) {
        return Promise.resolve();
    }

    const result = navigator.sendBeacon(
        process.env['NEXT_PUBLIC_LOG_BEACON_URL'],
        JSON.stringify(payload)
    );

    if (!result) {
        return Promise.reject(
            new Error('Unable to transmit logs to beacon: Response was not ok')
        );
    }

    payload.length = 0;

    return Promise.resolve();
}

async function browserTransmit(
    level: CustomLevel,
    { messages, bindings, level: logLevel }: LogEvent
) {
    if (!process.env['NEXT_PUBLIC_LOG_BEACON_URL']) {
        return Promise.reject(
            new Error('Missing NEXT_PUBLIC_LOG_BEACON_URL value')
        );
    }

    if (!process.env['NEXT_PUBLIC_BEACON_BATCH_SIZE']) {
        return Promise.reject(
            new Error('Missing NEXT_PUBLIC_BEACON_BATCH_SIZE value')
        );
    }

    if (
        logLevel.value <
        standardLevels.values[process.env['NEXT_PUBLIC_LOG_LEVEL'] || 'info']
    ) {
        return Promise.resolve();
    }

    // Typically when calling logger.info|warn|error we provide one or two args.
    // Either just the msg as a string as the first arg, logger.info('example')
    // or an object followed by a string, logger.info({ additional: 'data' }, 'example')
    const msg = messages.length === 1 ? messages[0] : messages[1];
    const additionalData = messages.length === 1 ? {} : messages[0];

    const logData = {
        level,
        msg,
        ...additionalData,
        ...metaInformation()
    };

    bindings.forEach((binding) => {
        Object.assign(logData, binding);
    });

    try {
        payload.push(logData);

        if (
            payload.length >=
            Number(process.env['NEXT_PUBLIC_BEACON_BATCH_SIZE'])
        ) {
            return flushBeaconQueue();
        }

        return Promise.resolve();
    } catch (err) {
        return Promise.reject(
            new Error(`Unable to transmit logs to the beacon: ${err}`)
        );
    }
}

export default browserTransmit;
